import { useEffect, useState } from "react";
import { useHttp } from "../../hooks";
import {
	useAlertContext,
	useClinicContext,
	useUserContext
} from "../../contexts";
import { useMsal } from "@azure/msal-react";
import {
	AlertType,
	BPDoctor,
	Clinic,
	DoctorRoomAllocation,
	GetBpDoctorsResponse
} from "../../models";
import CircularProgress from "@mui/material/CircularProgress";
import { apiAccessRequest } from "../../constants";
import { RoomAllocationTable } from "./RoomAllocationTable";

interface props {
	clinic: Clinic | undefined;
	setClinic: React.Dispatch<React.SetStateAction<Clinic | undefined>>;
}

export const DoctorRoomAllocations = ({ clinic, setClinic }: props) => {
	const { account } = useUserContext();
	const { instance } = useMsal();
	const { showAlert } = useAlertContext();
	const { userClinics } = useClinicContext();
	const [isSavingError, setIsSavingError] = useState(false);

	const getDoctorRoomAllocationsUrl = (clinicId: string) =>
		`${process.env.REACT_APP_CONSULTS_URL}ClinicRoomDoctor/clinic/${clinicId}`;
	const {
		data: doctorRoomAllocationData,
		isError: isFetchDoctorRoomAllocationError,
		isLoading: isLoading1,
		fetchData: getDoctorRoomAllocationData
	} = useHttp<DoctorRoomAllocation[]>(
		getDoctorRoomAllocationsUrl(clinic?.id.toString() || ""),
		{
			method: "GET"
		}
	);

	const getBpDoctorUrl = (clinicId: string) =>
		`${process.env.REACT_APP_CONSULTS_URL}Doctor/DoctorInstances?clinicId=${clinicId}`;
	const {
		data: doctorResponse,
		isError: isFetchDoctorsError,
		isLoading: isLoading2,
		fetchData: fetchDoctors
	} = useHttp<GetBpDoctorsResponse>(
		getBpDoctorUrl(clinic?.id.toString() || ""),
		{
			method: "GET"
		}
	);

	const [doctors, setDoctors] = useState<BPDoctor[]>([]);

	const [cellMessages, setCellMessages] = useState<
		Record<string, { message: string; type: "error" | "success" }>
	>({});

	useEffect(() => {
		if (!clinic && userClinics && userClinics.length > 0) {
			setClinic(userClinics[0]);
		}
	}, [clinic, setClinic, userClinics]);

	useEffect(() => {
		if (clinic) {
			getDoctorRoomAllocationData();
			fetchDoctors();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [clinic]);

	useEffect(() => {
		if (doctorResponse) {
			setDoctors(
				doctorResponse.value
					.filter((doctor) => doctor.takingFaceToFace)
					.sort((a, b) => (a.fullName < b.fullName ? -1 : 1))
			);
		}
	}, [doctorResponse]);

	const handleClinicChange = (value: string) => {
		setClinic(userClinics?.find((clinic) => clinic.id === Number(value)));
	};

	const saveDoctorRoomAllocation = async (
		doctorRoomAllocation: DoctorRoomAllocation
	) => {
		try {
			setIsSavingError(false);
			const requestUrl = `${process.env.REACT_APP_CONSULTS_URL}ClinicRoomDoctor`;
			const tokenResponse = await instance.acquireTokenSilent({
				account,
				scopes: apiAccessRequest.scopes
			});
			const headers = {
				Authorization: `Bearer ${tokenResponse.accessToken}`,
				"Content-Type": "application/json"
			};
			const response = await fetch(requestUrl, {
				method: doctorRoomAllocation.id === 0 ? "POST" : "PUT",
				headers,
				body: JSON.stringify([doctorRoomAllocation])
			});

			if (!response.ok) {
				throw new Error("Network response was not ok");
			}
			getDoctorRoomAllocationData();
		} catch (error) {
			console.info("fetchData catch", error);
			setIsSavingError(true);
		}
	};

	useEffect(() => {
		if (isFetchDoctorRoomAllocationError) {
			showAlert({
				message: "There was an error retrieving doctor room allocation data",
				alertType: AlertType.error
			});
			return;
		}
		if (isFetchDoctorsError) {
			showAlert({
				message: "There was an error fetching doctors",
				alertType: AlertType.error
			});
			return;
		}
		if (isSavingError) {
			showAlert({
				message: "There was an error saving the data",
				alertType: AlertType.error
			});
			return;
		}
	}, [
		isFetchDoctorsError,
		isFetchDoctorRoomAllocationError,
		isSavingError,
		showAlert
	]);

	if (isLoading1 || isLoading2 || !clinic) {
		return (
			<div className="icon-loading">
				<CircularProgress size={70} />
			</div>
		);
	} else {
		return (
			<RoomAllocationTable
				clinic={clinic}
				doctorRoomAllocationData={doctorRoomAllocationData ?? []}
				getDoctorRoomAllocationData={getDoctorRoomAllocationData}
				doctors={doctors}
				handleClinicChange={handleClinicChange}
				saveDoctorRoomAllocation={saveDoctorRoomAllocation}
				cellMessages={cellMessages}
				setCellMessages={setCellMessages}
			/>
		);
	}
};
